// src/pages/SiteHazardRegister.tsx — G-10: Site-wide Hazard / Risk Register
// Aggregates hazards from all SWMS per job. 5×5 risk matrix.
// CoP: How to Manage WHS Risks (Safe Work Australia 2021) · ISO 45001:2018 cl.6.1

import { useState } from 'react'
import { useAuthStore } from '../stores/auth.store'
import { MOCK_HAZARD_REGISTER, HAZARD_CATEGORIES, HazardEntry } from '../utils/mockDataWHSMgmt'
import { MOCK_JOBS } from '../utils/mockData'
import { Shield, Plus, Download, Filter, ChevronRight, X, AlertTriangle, Sparkles, Search, CheckCircle } from 'lucide-react'

const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-AU',{day:'numeric',month:'short',year:'2-digit'})

const riskColor = (r: number): { bg: string; text: string; label: string } => {
  if (r >= 15) return { bg: 'bg-red-100',    text: 'text-red-800',    label: 'EXTREME' }
  if (r >= 8)  return { bg: 'bg-orange-100', text: 'text-orange-800', label: 'HIGH' }
  if (r >= 4)  return { bg: 'bg-amber-100',  text: 'text-amber-700',  label: 'MEDIUM' }
  return             { bg: 'bg-green-100',  text: 'text-green-700',  label: 'LOW' }
}

const CONTROL_TYPE_META: Record<string, { label: string; bg: string; text: string }> = {
  ELIMINATE:  { label: 'Eliminate',    bg: 'bg-green-100',  text: 'text-green-800' },
  SUBSTITUTE: { label: 'Substitute',   bg: 'bg-blue-100',   text: 'text-blue-800' },
  ISOLATE:    { label: 'Isolate',      bg: 'bg-purple-100', text: 'text-purple-800' },
  ENGINEER:   { label: 'Engineering',  bg: 'bg-indigo-100', text: 'text-indigo-800' },
  ADMIN:      { label: 'Admin control',bg: 'bg-amber-100',  text: 'text-amber-800' },
  PPE:        { label: 'PPE',          bg: 'bg-gray-100',   text: 'text-gray-700' },
}

// ── 5×5 Risk Matrix ──────────────────────────────────────────────
function RiskMatrix({ hazards, onCellClick, activeCell }: { hazards: HazardEntry[]; onCellClick: (r: number) => void; activeCell: number | null }) {
  const cellCount = (l: number, c: number) => hazards.filter(h => h.residualLikelihood === l && h.residualConsequence === c && h.active).length
  const rating    = (l: number, c: number) => l * c
  const cellColor = (r: number) => {
    if (r >= 15) return 'bg-red-200 hover:bg-red-300 border-red-300'
    if (r >= 8)  return 'bg-orange-200 hover:bg-orange-300 border-orange-300'
    if (r >= 4)  return 'bg-amber-100 hover:bg-amber-200 border-amber-200'
    return             'bg-green-100 hover:bg-green-200 border-green-200'
  }
  const LIKELIHOOD_LABELS = ['','Rare','Unlikely','Possible','Likely','Almost\nCertain']
  const CONSEQUENCE_LABELS = ['','Negligible','Minor','Moderate','Major','Catastrophic']

  return (
    <div className="overflow-x-auto">
      <div className="text-xs font-semibold text-gray-600 mb-2 text-center">Residual Risk Distribution</div>
      <table className="border-collapse mx-auto">
        <thead>
          <tr>
            <th className="w-16 text-[10px] text-gray-400 text-right pr-2 pb-1">Likelihood ↑ / Consequence →</th>
            {[1,2,3,4,5].map(c => <th key={c} className="w-14 text-[10px] text-gray-500 font-medium text-center pb-1 px-1">{CONSEQUENCE_LABELS[c]}</th>)}
          </tr>
        </thead>
        <tbody>
          {[5,4,3,2,1].map(l => (
            <tr key={l}>
              <td className="text-[10px] text-gray-500 font-medium text-right pr-2 py-0.5">{LIKELIHOOD_LABELS[l]}</td>
              {[1,2,3,4,5].map(c => {
                const r = rating(l, c)
                const cnt = cellCount(l, c)
                const isActive = activeCell === r
                return (
                  <td key={c} onClick={() => onCellClick(isActive ? 0 : r)}
                    className={`w-14 h-10 border text-center cursor-pointer transition-all rounded ${cellColor(r)} ${isActive ? 'ring-2 ring-[#2E75B6] scale-105' : ''}`}>
                    {cnt > 0 && <span className="text-xs font-bold text-gray-700">{cnt}</span>}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex gap-3 justify-center mt-2">
        {[{r:15,l:'Extreme ≥15'},{r:8,l:'High 8–14'},{r:4,l:'Medium 4–7'},{r:1,l:'Low 1–3'}].map(({r,l}) => {
          const c = riskColor(r)
          return <div key={r} className="flex items-center gap-1"><div className={`w-3 h-3 rounded ${c.bg} border`}/><span className="text-[10px] text-gray-600">{l}</span></div>
        })}
      </div>
    </div>
  )
}

export function SiteHazardRegister() {
  const { isTier } = useAuthStore()
  const canEdit = isTier('B_PC') || isTier('B_PM') || isTier('A_DEVELOPER')
  const [hazards, setHazards] = useState<HazardEntry[]>(MOCK_HAZARD_REGISTER)
  const [jobFilter, setJobFilter]       = useState('ALL')
  const [categoryFilter, setCategoryFilter] = useState('ALL')
  const [ratingFilter, setRatingFilter] = useState(0)  // 0 = all
  const [search, setSearch]             = useState('')
  const [selected, setSelected]         = useState<HazardEntry | null>(null)
  const [showNew, setShowNew]           = useState(false)

  const activeJobs = MOCK_JOBS.filter(j => !['COMPLETED','CLOSED'].includes(j.status))
  const filtered = hazards.filter(h =>
    h.active &&
    (jobFilter === 'ALL'      || h.jobId === jobFilter) &&
    (categoryFilter === 'ALL' || h.hazardCategory === categoryFilter) &&
    (ratingFilter === 0       || h.residualRating === ratingFilter) &&
    (!search || h.hazardDescription.toLowerCase().includes(search.toLowerCase()) || h.activity.toLowerCase().includes(search.toLowerCase()))
  )

  const extreme = hazards.filter(h => h.active && h.residualRating >= 15).length
  const high    = hazards.filter(h => h.active && h.residualRating >= 8 && h.residualRating < 15).length

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-amber-500"/>Site Hazard Register</h1>
          <p className="text-sm text-gray-500 mt-0.5">Aggregated from approved SWMS · residual risk profile · 5×5 risk matrix · government audit ready</p>
        </div>
        <div className="flex gap-2">
          {canEdit && <button onClick={() => setShowNew(true)} className="btn-primary text-sm flex items-center gap-2"><Plus className="w-4 h-4"/>Add hazard</button>}
          <button className="btn-secondary text-sm flex items-center gap-2"><Download className="w-4 h-4"/>Export register</button>
        </div>
      </div>

      {extreme > 0 && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3 text-sm text-red-800"><AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0"/><strong>{extreme} extreme risk{extreme !== 1 ? 's' : ''}</strong> on register requiring urgent control review.</div>}

      <div className="grid grid-cols-4 gap-3">
        {[
          { label:'Extreme risk', value:extreme, color:'text-red-700', bg:'bg-red-50 border-red-200' },
          { label:'High risk',    value:high,    color:high>0?'text-orange-700':'text-gray-400', bg:high>0?'bg-orange-50 border-orange-200':'bg-white border-gray-200' },
          { label:'Total active', value:hazards.filter(h=>h.active).length, color:'text-gray-900', bg:'bg-white border-gray-200' },
          { label:'Source SWMS',  value:'3', color:'text-blue-700', bg:'bg-blue-50 border-blue-200' },
        ].map(({label,value,color,bg}) => (
          <div key={label} className={`border rounded-xl px-4 py-3 ${bg}`}><div className={`text-2xl font-bold ${color}`}>{value}</div><div className="text-xs font-medium text-gray-700 mt-0.5">{label}</div></div>
        ))}
      </div>

      {/* Risk matrix + filters */}
      <div className="grid grid-cols-5 gap-4">
        <div className="col-span-2 bg-white border border-gray-200 rounded-xl p-4">
          <RiskMatrix hazards={filtered} onCellClick={r => setRatingFilter(r)} activeCell={ratingFilter || null} />
          {ratingFilter > 0 && <button onClick={() => setRatingFilter(0)} className="mt-2 text-xs text-blue-600 hover:underline w-full text-center">Clear matrix filter</button>}
        </div>

        <div className="col-span-3 space-y-3">
          {/* Filters */}
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-32"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"/><input className="input pl-9 text-sm" placeholder="Search hazards…" value={search} onChange={e => setSearch(e.target.value)}/></div>
            <select className="input text-sm w-auto" value={jobFilter} onChange={e => setJobFilter(e.target.value)}><option value="ALL">All jobs</option>{activeJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}</select>
            <select className="input text-sm w-auto" value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}><option value="ALL">All categories</option>{HAZARD_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}</select>
          </div>

          {/* Hazard list */}
          <div className="space-y-2" style={{ maxHeight: '42vh', overflowY: 'auto' }}>
            {filtered.length === 0 && <div className="bg-white border border-dashed border-gray-300 rounded-xl py-10 text-center text-gray-400 text-sm">No hazards match current filters</div>}
            {filtered.map(h => {
              const before = riskColor(h.ratingBefore)
              const after  = riskColor(h.residualRating)
              const ctrl   = CONTROL_TYPE_META[h.controlType]
              const isSel  = selected?.id === h.id
              return (
                <div key={h.id} onClick={() => setSelected(isSel ? null : h)}
                  className={`bg-white border rounded-xl p-3 cursor-pointer transition-all hover:shadow-sm ${isSel ? 'border-blue-400 ring-2 ring-blue-100' : h.residualRating >= 15 ? 'border-red-300' : 'border-gray-200'}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{h.hazardCategory}</span>
                        <span className="text-[10px] font-mono text-gray-400">{h.jobNumber}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${after.bg} ${after.text}`}>Residual: {after.label} ({h.residualRating})</span>
                        {h.linkedSwmsId && <span className="text-[10px] text-blue-600">SWMS linked</span>}
                      </div>
                      <div className="font-medium text-gray-900 text-sm">{h.hazardDescription}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{h.activity} · Initial: {h.ratingBefore} → Residual: {h.residualRating}</div>
                    </div>
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isSel ? 'text-blue-500' : 'text-gray-400'}`} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <div className="bg-white border border-blue-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{selected.hazardCategory}</span>
                <span className="text-xs font-mono text-gray-400">{selected.jobNumber}</span>
                <span className="text-[10px] text-gray-400">Added {fmtDate(selected.addedAt)} by {selected.addedByName}</span>
              </div>
              <div className="font-semibold text-gray-900">{selected.hazardDescription}</div>
              <div className="text-sm text-gray-600 mt-0.5">{selected.activity}</div>
            </div>
            <button onClick={() => setSelected(null)}><X className="w-4 h-4 text-gray-400"/></button>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-2">Initial risk</div>
              <div className={`rounded-lg px-3 py-2 text-center ${riskColor(selected.ratingBefore).bg}`}>
                <div className={`text-xl font-bold ${riskColor(selected.ratingBefore).text}`}>{selected.ratingBefore}</div>
                <div className={`text-xs font-medium ${riskColor(selected.ratingBefore).text}`}>{riskColor(selected.ratingBefore).label}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">L{selected.likelihoodBefore} × C{selected.consequenceBefore}</div>
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-2">Controls applied</div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CONTROL_TYPE_META[selected.controlType].bg} ${CONTROL_TYPE_META[selected.controlType].text}`}>{CONTROL_TYPE_META[selected.controlType].label}</span>
              <ul className="mt-2 space-y-1">{selected.controls.map((c, i) => <li key={i} className="text-xs text-gray-700 flex items-start gap-1"><CheckCircle className="w-3 h-3 text-green-500 flex-shrink-0 mt-0.5"/>{c}</li>)}</ul>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-2">Residual risk</div>
              <div className={`rounded-lg px-3 py-2 text-center ${riskColor(selected.residualRating).bg}`}>
                <div className={`text-xl font-bold ${riskColor(selected.residualRating).text}`}>{selected.residualRating}</div>
                <div className={`text-xs font-medium ${riskColor(selected.residualRating).text}`}>{riskColor(selected.residualRating).label}</div>
                <div className="text-[10px] text-gray-500 mt-0.5">L{selected.residualLikelihood} × C{selected.residualConsequence}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New hazard form */}
      {showNew && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between"><h4 className="font-semibold text-blue-900">Add standalone hazard</h4><button onClick={() => setShowNew(false)}><X className="w-4 h-4 text-gray-400"/></button></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Job</label><select className="input text-sm">{activeJobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber}</option>)}</select></div>
            <div><label className="block text-xs font-medium text-gray-700 mb-1">Hazard category</label><select className="input text-sm">{HAZARD_CATEGORIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Activity / work task</label><input className="input text-sm" placeholder="What task does this hazard arise from?"/></div>
            <div className="col-span-2"><label className="block text-xs font-medium text-gray-700 mb-1">Hazard description</label><textarea className="input text-sm h-16 resize-none" placeholder="Describe the specific hazard and how harm could occur…"/></div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-2"><Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5 text-[#2E75B6]"/>WHS AI Agent (G-17) will pre-populate hazards from uploaded site drawings before mobilisation.</div>
          <div className="flex justify-end gap-2"><button onClick={() => setShowNew(false)} className="btn-secondary text-sm">Cancel</button><button onClick={() => setShowNew(false)} className="btn-primary text-sm">Add hazard</button></div>
        </div>
      )}
    </div>
  )
}
